import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getBillingDiagnostics } from "@/lib/billing-diagnostics.functions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { CheckCircle2, XCircle, AlertCircle, ArrowLeft, RefreshCw, Loader2 } from "lucide-react";

export const Route = createFileRoute("/settings/billing/diagnostics")({
  head: () => ({ meta: [{ title: "Diagnóstico de billing — Viny Club" }] }),
  component: BillingDiagnosticsPage,
});

function BillingDiagnosticsPage() {
  const { isAuthenticated } = useCurrentUser();
  const fetchDiag = useServerFn(getBillingDiagnostics);
  const q = useQuery({
    queryKey: ["billing-diagnostics"],
    queryFn: () => fetchDiag(),
    enabled: isAuthenticated,
    refetchInterval: 5000,
  });

  if (!isAuthenticated) {
    return (
      <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-8 text-center">
        <p className="text-muted-foreground">Entre para ver o diagnóstico.</p>
      </div>
    );
  }

  const d = q.data;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/settings/billing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> Plano
          </Link>
          <h1 className="mt-3 font-display text-3xl font-medium tracking-[-0.03em] lg:text-4xl">
            Diagnóstico de billing
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Estado real do seu Premium, da assinatura no Stripe e dos eventos de webhook recebidos.
            Atualiza a cada 5s.
          </p>
        </div>
        <button
          onClick={() => q.refetch()}
          disabled={q.isFetching}
          className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-4 py-2 text-xs font-medium hover:bg-foreground/[0.04]"
        >
          {q.isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Atualizar
        </button>
      </div>

      {q.isLoading && <Card>Carregando…</Card>}
      {q.error && <Card tone="error">Erro: {(q.error as Error).message}</Card>}

      {d && (
        <>
          <Card>
            <Row label="User ID" mono value={d.user_id} />
            <Row label="Email" value={d.email ?? "—"} />
            <Row
              label="Premium ativo (DB)"
              value={
                d.is_premium ? (
                  <span className="inline-flex items-center gap-1.5 text-green-500">
                    <CheckCircle2 className="h-4 w-4" /> Sim
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-red-500">
                    <XCircle className="h-4 w-4" /> Não
                  </span>
                )
              }
            />
          </Card>

          <Section title="Linha em `subscriptions`">
            {d.subscription_row_json ? (
              <Card>
                <pre className="max-h-80 overflow-auto text-xs leading-relaxed">
                  {JSON.stringify(JSON.parse(d.subscription_row_json), null, 2)}
                </pre>
              </Card>
            ) : (
              <Card tone="warn">
                <AlertCircle className="mr-2 inline h-4 w-4" />
                Nenhuma linha. O webhook não fez upsert ainda — ou nunca chegou.
              </Card>
            )}
          </Section>

          <Section title="Price IDs configurados">
            <Card>
              <Row label="STRIPE_PRICE_MONTHLY" mono value={d.configured_price_ids.monthly ?? "(vazio)"} />
              <Row label="STRIPE_PRICE_YEARLY" mono value={d.configured_price_ids.yearly ?? "(vazio)"} />
            </Card>
          </Section>

          <Section title="Customers no Stripe (busca por email)">
            {!d.stripe.ok ? (
              <Card tone="error">
                Falha ao consultar Stripe: {d.stripe.error}
                <p className="mt-2 text-xs text-muted-foreground">
                  Provável causa: STRIPE_SECRET_KEY ausente, inválido ou apontando para outra conta.
                </p>
              </Card>
            ) : d.stripe.customers.length === 0 ? (
              <Card tone="warn">
                Nenhum customer encontrado para {d.email}. Inicie um checkout para criar.
              </Card>
            ) : (
              <div className="space-y-3">
                {d.stripe.customers.map((c) => (
                  <Card key={c.id}>
                    <Row label="Customer ID" mono value={c.id} />
                    <Row label="metadata.user_id" mono value={c.metadata_user_id ?? "(vazio)"} />
                    <Row
                      label="Vincula a este usuário?"
                      value={
                        c.metadata_user_id === d.user_id ? (
                          <span className="inline-flex items-center gap-1.5 text-green-500">
                            <CheckCircle2 className="h-4 w-4" /> Sim
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-amber-500">
                            <AlertCircle className="h-4 w-4" /> Não — webhook não vai atualizar
                          </span>
                        )
                      }
                    />
                    {c.subscriptions.length > 0 && (
                      <div className="mt-3 border-t border-foreground/10 pt-3">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                          Assinaturas
                        </div>
                        {c.subscriptions.map((s) => (
                          <div key={s.id} className="mt-2 rounded-lg bg-foreground/[0.03] p-3 text-xs">
                            <div className="font-mono">{s.id}</div>
                            <div className="mt-1 text-muted-foreground">
                              status: <span className="text-foreground">{s.status}</span>
                              {" · "}intervalo:{" "}
                              <span className="text-foreground">{s.interval ?? "—"}</span>
                              {" · "}cancel_at_period_end:{" "}
                              <span className="text-foreground">
                                {String(s.cancel_at_period_end)}
                              </span>
                            </div>
                            <div className="text-muted-foreground">
                              price:{" "}
                              <span className="font-mono text-foreground">{s.price_id ?? "—"}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </Section>

          <Section title={`Eventos de webhook recebidos (${d.webhook_events.length})`}>
            {d.webhook_events.length === 0 ? (
              <Card tone="warn">
                Nenhum evento gravado. Possíveis causas:
                <ul className="ml-4 mt-2 list-disc text-xs text-muted-foreground">
                  <li>Webhook do Stripe não está apontando para esta URL</li>
                  <li>STRIPE_WEBHOOK_SECRET diferente do configurado no Stripe</li>
                  <li>Você ainda não passou por checkout</li>
                </ul>
              </Card>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-foreground/10">
                <table className="w-full text-xs">
                  <thead className="bg-foreground/[0.04] text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Quando</th>
                      <th className="px-3 py-2">Evento</th>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.webhook_events.map((e) => (
                      <tr key={e.id} className="border-t border-foreground/10">
                        <td className="px-3 py-2 text-muted-foreground">
                          {new Date(e.received_at).toLocaleString("pt-BR")}
                        </td>
                        <td className="px-3 py-2 font-mono">{e.event_type}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">
                          {e.stripe_customer_id ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          {e.processing_error ? (
                            <span className="text-red-500">{e.processing_error}</span>
                          ) : (
                            <span className="text-green-500">ok</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Card({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "warn" | "error";
}) {
  const toneClass =
    tone === "error"
      ? "border-red-500/30 bg-red-500/[0.06]"
      : tone === "warn"
        ? "border-amber-500/30 bg-amber-500/[0.06]"
        : "border-foreground/10 bg-foreground/[0.02]";
  return <div className={`rounded-2xl border p-5 ${toneClass}`}>{children}</div>;
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}
